import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

// USBC Oil Pattern Conversion Formulas
const convertOilPatternAverage = (average: number, fromPattern: string, toPattern: string): number | null => {
  if (average <= 0 || fromPattern === toPattern) return null;
  
  const baseline = 110;
  const diff = average - baseline;
  
  const from = fromPattern.toLowerCase();
  const to = toPattern.toLowerCase();
  
  if (from === 'sport' && to === 'challenge') return Math.round(average + (diff * 0.091));
  if (from === 'sport' && (to === 'standard' || to === 'house')) return Math.round(average + (diff * 0.182));
  if (from === 'challenge' && to === 'sport') return Math.round(average - (diff * 0.091));
  if (from === 'challenge' && (to === 'standard' || to === 'house')) return Math.round(average + (diff * 0.1));
  if ((from === 'standard' || from === 'house') && to === 'sport') return Math.round(average - (diff * 0.182));
  if ((from === 'standard' || from === 'house') && to === 'challenge') return Math.round(average - (diff * 0.1));
  
  return null;
};

interface LeagueStat {
  leagueId: number;
  leagueName: string;
  average: number;
  handicap: number;
  gamesPlayed: number;
  lastCalculated: string;
}

interface BowlerStats {
  bowler: {
    lifetimeAverage: number;
    lifetimeHandicap: number;
  };
  globalAggregateStats: {
    weeklyAverage: number | string;
    weeklyHandicap: number | string;
    monthlyAverage: number | string;
    monthlyHandicap: number | string;
    yearlyAverage: number | string;
    yearlyHandicap: number | string;
  };
  allLeagueStats: LeagueStat[];
}

interface LeagueAnalytics {
    leagueId: number;
    leagueName: string;
    totalSpent: number;
    totalEarned: number;
    avgScore: number;
}

interface BowlerAverage {
    bowlerId: number;
    name: string;
    avgScore: number;
}

interface TeamMember {
    bowlerId: number;
    name: string;
    avgScore: number;
    overPar: boolean;
}

interface TeamStat {
    teamId: number;
    teamName: string;
    leagueId: number;
    leagueName: string;
    placement: number;
    points: number;
    members: TeamMember[];
    mvpId: number;
}

interface GameDetail {
    gameNumber: number;
    score: number;
}

interface SquadDetail {
    squadId: number;
    squadName: string;
    squadDate: string;
    games: GameDetail[];
    seriesTotal: number;
}

interface PlayerGameDetails {
    teamName: string;
    leagueName: string;
    squads: SquadDetail[];
}

interface SideEventEarning {
    sideEventId: number;
    sideEventName: string;
    sideEventType: string;
    totalEarned: number;
}

interface PlayerAnalyticsData {
    leagues: LeagueAnalytics[];
    bowlerAverages: BowlerAverage[];
    teamStats: TeamStat[];
    mvpAwards: number;
    sideEventEarnings: SideEventEarning[];
    threeHundredGames: number;
    leaguesWon: number;
    leaguesMostPins: number;
}

const PlayerAnalytics: React.FC = () => {
    const [data, setData] = useState<PlayerAnalyticsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [openSideEventTypes, setOpenSideEventTypes] = useState<{ [type: string]: boolean }>({});
    const [showOnlyTeamsWithGames, setShowOnlyTeamsWithGames] = useState(true);
    const [selectedPlayerDetails, setSelectedPlayerDetails] = useState<PlayerGameDetails | null>(null);
    const [playerDetailsLoading, setPlayerDetailsLoading] = useState(false);
    const [showPlayerModal, setShowPlayerModal] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Stats section state
    const [bowlerStats, setBowlerStats] = useState<BowlerStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [selectedGlobalTimeframe, setSelectedGlobalTimeframe] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');
    const [selectedAllLeagueId, setSelectedAllLeagueId] = useState<string>('all');
    
    const [oilPatternStats, setOilPatternStats] = useState<any>(null);
    const [oilPatternLoading, setOilPatternLoading] = useState(false);
    const [selectedOilPeriod, setSelectedOilPeriod] = useState<'all_time' | 'last_year' | 'last_month' | 'last_week'>('all_time');
    const [selectedOilPattern, setSelectedOilPattern] = useState<'all' | 'House' | 'Challenge' | 'Sport' | 'Not Set'>('all');
    const [oilBaseAverage, setOilBaseAverage] = useState<number>(200);
    const [oilHandicapPercentage, setOilHandicapPercentage] = useState<number>(80);

    const getSideEventTypeTranslation = (type: string): string => {
        const typeMap: { [key: string]: string } = {
            'bracket': t('admin.analytics.sideEventTypes.bracket'),
            'high_game': t('admin.analytics.sideEventTypes.highGame'),
            'high_series': t('admin.analytics.sideEventTypes.highSeries'),
            'mystery_score': t('admin.analytics.sideEventTypes.mysteryScore'),
            'mystery_series': t('admin.analytics.sideEventTypes.mysterySeries'),
            'clean_game': t('admin.analytics.sideEventTypes.cleanGame'),
            'side_pots': t('admin.analytics.sideEventTypes.sidePots'),
            'survivor': t('admin.analytics.sideEventTypes.survivor'),
            'eliminator': t('admin.analytics.sideEventTypes.eliminator')
        };
        return typeMap[type.toLowerCase()] || type.replace(/_/g, ' ');
    };

    const fetchBowlerStats = async () => {
        try {
            setStatsLoading(true);
            const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
            const response = await fetch(`${apiUrl}/player/detailed-stats`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.ok) {
                const data = await response.json();
                setBowlerStats(data);
            }
        } catch (error) {
            console.error('Failed to fetch bowler stats:', error);
        } finally {
            setStatsLoading(false);
        }
    };

    const fetchOilPatternStats = async () => {
        setOilPatternLoading(true);
        try {
            const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
            const params = new URLSearchParams({
                period: selectedOilPeriod,
                oilPattern: selectedOilPattern,
                baseAverage: oilBaseAverage.toString(),
                handicapPercentage: oilHandicapPercentage.toString()
            });

            const response = await fetch(`${apiUrl}/player/oil-pattern-performance?${params}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.ok) {
                const data = await response.json();
                setOilPatternStats(data);
            }
        } catch (error) {
            console.error('Failed to fetch oil pattern stats:', error);
        } finally {
            setOilPatternLoading(false);
        }
    };

    const getDisplayedAllLeaguesStats = () => {
        if (!bowlerStats?.allLeagueStats) return [];

        if (selectedAllLeagueId === 'all') {
            return bowlerStats.allLeagueStats;
        }
        return bowlerStats.allLeagueStats.filter(stat => stat.leagueId.toString() === selectedAllLeagueId);
    };

    useEffect(() => {
        const fetchAnalytics = async () => {
            setLoading(true);
            const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
            const res = await fetch(`${apiUrl}/bowler/analytics`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });
            const json = await res.json();
            setData(json);
            setLoading(false);
        };
        fetchAnalytics();
        fetchBowlerStats();
    }, []);

    useEffect(() => {
        if (data) {
            fetchOilPatternStats();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOilPeriod, selectedOilPattern, oilBaseAverage, oilHandicapPercentage]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
                <div className="text-white text-xl font-bold">{t('player.loadingAnalytics')}</div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
                <div className="text-red-400 text-xl font-bold">{t('player.failedToLoadAnalytics')}</div>
            </div>
        );
    }

    // Group side event earnings by type
    const filteredSideEvents = data.sideEventEarnings.filter(se => se.totalEarned > 0);
    const groupedSideEvents: { [type: string]: SideEventEarning[] } = {};
    filteredSideEvents.forEach(se => {
        if (!groupedSideEvents[se.sideEventType]) groupedSideEvents[se.sideEventType] = [];
        groupedSideEvents[se.sideEventType].push(se);
    });

    const toggleSideEventType = (type: string) => {
        setOpenSideEventTypes(prev => ({ ...prev, [type]: !prev[type] }));
    };

    const fetchPlayerGameDetails = async (teamId: number, bowlerId: number) => {
        setPlayerDetailsLoading(true);
        setShowPlayerModal(true);
        try {
            const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
            const response = await fetch(`${apiUrl}/bowler/team-game-details/${teamId}/${bowlerId}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });

            if (response.ok) {
                const details = await response.json();
                setSelectedPlayerDetails(details);
            }
        } catch (error) {
            console.error('Failed to fetch player game details:', error);
        } finally {
            setPlayerDetailsLoading(false);
        }
    };

    const closePlayerModal = () => {
        setShowPlayerModal(false);
        setSelectedPlayerDetails(null);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 py-10 px-4">
            <div className="max-w-full mx-auto space-y-10">
                {/* Header */}
                <div className="flex items-center gap-4 mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="text-white hover:text-blue-300 transition duration-200"
                        aria-label="Go back"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className="text-3xl font-bold text-white mb-0">{t('player.yourBowlingCareer')} 🎳</h1>
                </div>

                {/* Detailed Stats Section */}
                <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20">
                    <h2 className="text-2xl font-bold text-white mb-6">{t('player.detailedStats')}</h2>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <h3 className="text-sm font-semibold text-white mb-2">{t('scout.bowlerDetail.lifetimeStats')}</h3>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="bg-white/5 rounded-lg p-2 border border-white/10">
                                            <div className="text-center">
                                                <p className="text-blue-200 text-xs mb-1">{t('scout.bowlerDetail.avg')}</p>
                                                <p className="text-lg font-bold text-white">{bowlerStats?.bowler?.lifetimeAverage ?? 0}</p>
                                            </div>
                                        </div>
                                        <div className="bg-white/5 rounded-lg p-2 border border-white/10">
                                            <div className="text-center">
                                                <p className="text-blue-200 text-xs mb-1">{t('scout.bowlerDetail.hdcp')}</p>
                                                <p className="text-lg font-bold text-white">{bowlerStats?.bowler?.lifetimeHandicap ?? 0}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3">
                                    <h3 className="text-lg font-semibold text-white whitespace-nowrap">{t('scout.bowlerDetail.allLeaguesCombined')}</h3>
                                    <select value={selectedGlobalTimeframe} onChange={(e) => setSelectedGlobalTimeframe(e.target.value as 'weekly' | 'monthly' | 'yearly')} className="bg-blue-600/30 text-white text-sm rounded-lg px-3 py-2 border border-blue-500/50 focus:outline-none focus:border-blue-400 hover:bg-blue-600/40 transition cursor-pointer appearance-none w-full sm:w-auto" style={{backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem'}}>
                                        <option value="weekly" className="bg-blue-900 text-white">{t('scout.bowlerDetail.lastWeek')}</option>
                                        <option value="monthly" className="bg-blue-900 text-white">{t('scout.bowlerDetail.lastMonth')}</option>
                                        <option value="yearly" className="bg-blue-900 text-white">{t('scout.bowlerDetail.lastYear')}</option>
                                    </select>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                                        <div className="text-center">
                                            <p className="text-blue-200 text-xs mb-1">{t('scout.bowlerDetail.average')}</p>
                                            <p className="text-xl font-bold text-white">
                                                {bowlerStats?.globalAggregateStats?.[selectedGlobalTimeframe === 'weekly' ? 'weeklyAverage' : selectedGlobalTimeframe === 'monthly' ? 'monthlyAverage' : 'yearlyAverage'] ?? 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                                        <div className="text-center">
                                            <p className="text-blue-200 text-xs mb-1">{t('scout.bowlerDetail.handicap')}</p>
                                            <p className="text-xl font-bold text-white">
                                                {bowlerStats?.globalAggregateStats?.[selectedGlobalTimeframe === 'weekly' ? 'weeklyHandicap' : selectedGlobalTimeframe === 'monthly' ? 'monthlyHandicap' : 'yearlyHandicap'] ?? 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                                    <h3 className="text-lg font-semibold text-white">{t('scout.bowlerDetail.allLeaguesBreakdown')}</h3>
                                    <select value={selectedAllLeagueId} onChange={(e) => setSelectedAllLeagueId(e.target.value)} className="bg-blue-600/30 text-white text-xs rounded-lg px-2 py-1.5 border border-blue-500/50 focus:outline-none focus:border-blue-400 hover:bg-blue-600/40 transition cursor-pointer appearance-none w-full sm:w-auto" style={{backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.25rem center', backgroundSize: '1em 1em', paddingRight: '1.5rem'}}>
                                        <option value="all" className="bg-blue-900 text-white">{t('scout.bowlerDetail.allLeagues')}</option>
                                        {bowlerStats?.allLeagueStats?.map((stat: LeagueStat) => (
                                            <option key={stat.leagueId} value={stat.leagueId} className="bg-blue-900 text-white">{stat.leagueName}</option>
                                        ))}
                                    </select>
                                </div>
                                
                                {statsLoading ? (
                                    <div className="bg-white/5 rounded-lg p-6 border border-white/10 text-center">
                                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto mb-2"></div>
                                        <p className="text-blue-200 text-xs">{t('scout.bowlerDetail.loadingStats')}</p>
                                    </div>
                                ) : getDisplayedAllLeaguesStats().length === 0 ? (
                                    <div className="bg-white/5 rounded-lg p-6 border border-white/10 text-center">
                                        <p className="text-blue-200 text-sm">{t('scout.bowlerDetail.noStatsAvailable')}</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2" style={{scrollbarWidth: 'thin', scrollbarColor: 'rgba(59, 130, 246, 0.5) rgba(255, 255, 255, 0.1)'}}>
                                        {getDisplayedAllLeaguesStats().map((stat: any) => (
                                            <div key={stat.leagueId} className={stat.leagueDescription === 'Side Action Only' ? 'bg-orange-500/10 rounded-lg p-3 border border-orange-400/40' : 'bg-white/5 rounded-lg p-3 border border-white/10'}>
                                                <div className="flex items-center justify-between mb-2">
                                                    <h4 className="text-white font-semibold text-sm">{stat.leagueName}</h4>
                                                    <span className={stat.leagueDescription === 'Side Action Only' ? 'text-orange-300 text-xs' : 'text-blue-300 text-xs'}>{stat.leagueDescription === 'Side Action Only' ? `Side Action Only: ${stat.gamesPlayed}` : `${stat.gamesPlayed} ${stat.gamesPlayed === 1 ? 'game' : 'games'}`}</span>
                                                </div>
                                                <div className="flex gap-2 items-center">
                                                    <div className="bg-white/5 rounded-lg p-2 border border-white/10 min-w-[75px]">
                                                        <p className="text-blue-200 text-xs mb-1 text-center">Avg</p>
                                                        <p className="text-lg font-bold text-white text-center">{stat.average > 0 ? stat.average : 'N/A'}</p>
                                                    </div>
                                                    <div className="bg-white/5 rounded-lg p-2 border border-white/10 min-w-[75px]">
                                                        <p className="text-blue-200 text-xs mb-1 text-center">Hdcp</p>
                                                        <p className="text-lg font-bold text-white text-center">{stat.handicap}</p>
                                                    </div>
                                                    <div className="flex-1 text-right">
                                                        <p className="text-blue-400 text-xs">{new Date(stat.lastCalculated).toLocaleDateString()}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-lg font-semibold text-white mb-3">{t('scout.bowlerDetail.oilPatternStats')}</h3>
                            
                            <div className="space-y-3 mb-4">
                                <div className="flex gap-2 flex-wrap">
                                    <select value={selectedOilPeriod} onChange={(e) => setSelectedOilPeriod(e.target.value as any)} className="bg-blue-600/30 text-white text-sm rounded-lg px-3 py-2 border border-blue-500/50 focus:outline-none focus:border-blue-400 hover:bg-blue-600/40 transition cursor-pointer appearance-none" style={{backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem'}}>
                                        <option value="all_time" className="bg-blue-900 text-white">{t('scout.bowlerDetail.allTime')}</option>
                                        <option value="last_year" className="bg-blue-900 text-white">{t('scout.bowlerDetail.lastYear')}</option>
                                        <option value="last_month" className="bg-blue-900 text-white">{t('scout.bowlerDetail.lastMonth')}</option>
                                        <option value="last_week" className="bg-blue-900 text-white">{t('scout.bowlerDetail.lastWeek')}</option>
                                    </select>

                                    <select value={selectedOilPattern} onChange={(e) => setSelectedOilPattern(e.target.value as any)} className="bg-blue-600/30 text-white text-sm rounded-lg px-3 py-2 border border-blue-500/50 focus:outline-none focus:border-blue-400 hover:bg-blue-600/40 transition cursor-pointer appearance-none" style={{backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'white\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem'}}>
                                        <option value="all" className="bg-blue-900 text-white">{t('scout.bowlerDetail.allPatterns')}</option>
                                        <option value="House" className="bg-blue-900 text-white">House</option>
                                        <option value="Challenge" className="bg-blue-900 text-white">Challenge</option>
                                        <option value="Sport" className="bg-blue-900 text-white">Sport</option>
                                        <option value="Not Set" className="bg-blue-900 text-white">{t('scout.bowlerDetail.notSet')}</option>
                                    </select>
                                </div>

                                <div className="flex gap-2 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <label className="text-blue-200 text-sm whitespace-nowrap">Base Avg:</label>
                                        <input type="number" min="100" max="300" value={oilBaseAverage} onChange={(e) => setOilBaseAverage(parseInt(e.target.value) || 200)} className="bg-blue-600/30 text-white text-sm rounded-lg px-3 py-2 border border-blue-500/50 focus:outline-none focus:border-blue-400 hover:bg-blue-600/40 transition w-20" />
                                    </div>
                                    
                                    <div className="flex items-center gap-2">
                                        <label className="text-blue-200 text-sm whitespace-nowrap">Hdcp %:</label>
                                        <input type="number" min="0" max="100" value={oilHandicapPercentage} onChange={(e) => setOilHandicapPercentage(parseInt(e.target.value) || 80)} className="bg-blue-600/30 text-white text-sm rounded-lg px-3 py-2 border border-blue-500/50 focus:outline-none focus:border-blue-400 hover:bg-blue-600/40 transition w-20" />
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 mb-4 items-start">
                                <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-[100px]">
                                    <div className="text-center">
                                        <p className="text-blue-200 text-xs mb-1">{t('scout.bowlerDetail.cumulativeAvg')}</p>
                                        <p className="text-xl font-bold text-white">{oilPatternLoading ? '...' : oilPatternStats?.cumulativeAverage || 'N/A'}</p>
                                    </div>
                                </div>
                                <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-[100px]">
                                    <div className="text-center">
                                        <p className="text-blue-200 text-xs mb-1">{t('scout.bowlerDetail.cumulativeHdcp')}</p>
                                        <p className="text-xl font-bold text-white">{oilPatternLoading ? '...' : oilPatternStats?.cumulativeHandicap || 'N/A'}</p>
                                    </div>
                                </div>
                                {!oilPatternLoading && oilPatternStats?.cumulativeAverage && selectedOilPattern !== 'all' && selectedOilPattern !== 'Not Set' && (
                                    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                                        <p className="text-blue-200 text-xs mb-1">{t('scout.bowlerDetail.patternEquivalents')}</p>
                                        <div className="text-xs text-blue-300/80 space-y-1">
                                            {selectedOilPattern.toLowerCase() === 'sport' && (
                                                <>
                                                    <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'sport', 'challenge')} Challenge</div>
                                                    <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'sport', 'house')} House</div>
                                                </>
                                            )}
                                            {selectedOilPattern.toLowerCase() === 'challenge' && (
                                                <>
                                                    <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'challenge', 'sport')} Sport</div>
                                                    <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'challenge', 'house')} House</div>
                                                </>
                                            )}
                                            {selectedOilPattern.toLowerCase() === 'house' && (
                                                <>
                                                    <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'house', 'challenge')} Challenge</div>
                                                    <div>≈ {convertOilPatternAverage(Number(oilPatternStats.cumulativeAverage), 'house', 'sport')} Sport</div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {oilPatternLoading ? (
                                <div className="bg-white/5 rounded-lg p-6 border border-white/10 text-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mx-auto mb-2"></div>
                                    <p className="text-blue-200 text-xs">{t('scout.bowlerDetail.loadingStats')}</p>
                                </div>
                            ) : !oilPatternStats?.leagues || oilPatternStats.leagues.length === 0 ? (
                                <div className="bg-white/5 rounded-lg p-6 border border-white/10 text-center">
                                    <div className="text-3xl mb-2">🎳</div>
                                    <p className="text-blue-200 text-sm">{t('scout.bowlerDetail.noOilPatternData')}</p>
                                </div>
                            ) : (
                                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2" style={{scrollbarWidth: 'thin', scrollbarColor: 'rgba(59, 130, 246, 0.5) rgba(255, 255, 255, 0.1)'}}>
                                    {oilPatternStats.leagues.map((league: any) => (
                                        <div key={`${league.leagueId}-${league.oilPattern}`} className={league.leagueDescription === 'Side Action Only' ? 'bg-orange-500/10 rounded-lg p-4 border border-orange-400/40' : 'bg-white/5 rounded-lg p-4 border border-white/10'}>
                                            <div className="flex items-center justify-between mb-3">
                                                <div>
                                                    <h4 className="text-white font-semibold">{league.leagueName}</h4>
                                                    <p className="text-blue-300 text-xs">🎳 {league.oilPattern === 'Not Set' ? t('scout.bowlerDetail.notSet') : league.oilPattern}</p>
                                                </div>
                                                <span className={league.leagueDescription === 'Side Action Only' ? 'text-orange-300 text-sm' : 'text-blue-300 text-sm'}>{league.leagueDescription === 'Side Action Only' ? `Side Action Only: ${league.gamesPlayed}` : `${league.gamesPlayed} ${league.gamesPlayed === 1 ? 'game' : 'games'}`}</span>
                                            </div>
                                            <div className="flex gap-3 items-start">
                                                <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-[90px]">
                                                    <p className="text-blue-200 text-xs mb-1 text-center">{t('scout.bowlerDetail.average')}</p>
                                                    <p className="text-xl font-bold text-white text-center">{league.average > 0 ? league.average : 'N/A'}</p>
                                                </div>
                                                <div className="bg-white/5 rounded-lg p-3 border border-white/10 min-w-[90px]">
                                                    <p className="text-blue-200 text-xs mb-1 text-center">{t('scout.bowlerDetail.handicap')}</p>
                                                    <p className="text-xl font-bold text-white text-center">{league.handicap}</p>
                                                </div>
                                                {league.average > 0 && league.oilPattern !== 'Not Set' && (
                                                    <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                                                        <p className="text-blue-200 text-[10px] mb-1">{t('scout.bowlerDetail.equivalents')}</p>
                                                        <div className="text-[10px] text-blue-300/70 space-y-0.5">
                                                            {league.oilPattern.toLowerCase() === 'sport' && (
                                                                <>
                                                                    <div>≈ {convertOilPatternAverage(Number(league.average), 'sport', 'challenge')} Challenge</div>
                                                                    <div>≈ {convertOilPatternAverage(Number(league.average), 'sport', 'house')} House</div>
                                                                </>
                                                            )}
                                                            {league.oilPattern.toLowerCase() === 'challenge' && (
                                                                <>
                                                                    <div>≈ {convertOilPatternAverage(Number(league.average), 'challenge', 'sport')} Sport</div>
                                                                    <div>≈ {convertOilPatternAverage(Number(league.average), 'challenge', 'house')} House</div>
                                                                </>
                                                            )}
                                                            {(league.oilPattern.toLowerCase() === 'house' || league.oilPattern.toLowerCase() === 'standard') && (
                                                                <>
                                                                    <div>≈ {convertOilPatternAverage(Number(league.average), 'house', 'challenge')} Challenge</div>
                                                                    <div>≈ {convertOilPatternAverage(Number(league.average), 'house', 'sport')} Sport</div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Team Performance Statistics */}
                {data.teamStats.length > 0 && (
                    <div className="bg-white/10 rounded-xl p-6 border border-white/10 shadow-lg">
                        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
                            <div className="flex flex-col gap-3">
                                <h2 className="text-lg font-bold text-white">
                                    {t('player.careerPerformance')}
                                </h2>
                                <div className="flex flex-wrap gap-2">
                                    <span className="bg-white/10 text-white font-bold px-3 py-1 rounded-full border border-white/20 text-center whitespace-nowrap">
                                        <span className="text-md">👑</span> <span className="text-sm">{t('player.mvpCrowns')}: {data.mvpAwards}</span>
                                    </span>

                                    <span className="bg-white/10 text-white font-bold px-3 py-1 rounded-full border border-white/20 text-center whitespace-nowrap">
                                        <span className="text-md">🌟</span> <span className="text-sm">{t('player.threeHundredGames')}: {data.threeHundredGames}</span>
                                    </span>

                                    <span className="bg-white/10 text-white font-bold px-3 py-1 rounded-full border border-white/20 text-center whitespace-nowrap">
                                        <span className="text-md">🥇</span> <span className="text-sm">{t('player.leaguesWon')}: {data.leaguesWon}</span>
                                    </span>

                                    <span className="bg-white/10 text-white font-bold px-3 py-1 rounded-full border border-white/20 text-center whitespace-nowrap">
                                        <span className="text-md">🏆</span> <span className="text-sm">{t('player.bestLeagueBowler')}: {data.leaguesMostPins}</span>
                                    </span>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-3">
                                <label className="text-white text-sm font-medium">{t('player.teamsWithGamesRecorded')}</label>
                                <button
                                    onClick={() => setShowOnlyTeamsWithGames(!showOnlyTeamsWithGames)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                        showOnlyTeamsWithGames ? 'bg-blue-600' : 'bg-gray-600'
                                    }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                            showOnlyTeamsWithGames ? 'translate-x-6' : 'translate-x-1'
                                        }`}
                                    />
                                </button>
                            </div>
                        </div>
                        <div
                            className="space-y-6 max-h-96 overflow-y-auto pr-2"
                            style={{
                                scrollbarColor: "#a78bfa #312e81",
                                scrollbarWidth: "thin"
                            }}
                        >
                            {data.teamStats
                                .slice() // copy array to avoid mutating original
                                .filter(team => {
                                    if (!showOnlyTeamsWithGames) return true;
                                    return team.members.some(m => m.avgScore > 0);
                                })
                                .sort((a, b) => {
                                    const maxA = Math.max(...a.members.map(m => m.avgScore ?? 0));
                                    const maxB = Math.max(...b.members.map(m => m.avgScore ?? 0));
                                    return maxB - maxA;
                                })
                                .map(team => {
                                    const allZero = team.members.every(m => m.avgScore === 0);
                                    return (
                                        <div key={team.teamId} className="bg-black/30 rounded-lg p-4 shadow border border-black/30">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="text-lg font-bold text-white">{team.teamName}</div>
                                                <div className="flex flex-col items-end">
                                                    <div className="text-sm text-blue-300">{team.leagueName}</div>
                                                    {team.placement > 0 && (
                                                        <div className="text-xs text-yellow-300 mt-1">
                                                            {team.placement === 1 ? '🥇' : team.placement === 2 ? '🥈' : team.placement === 3 ? '🥉' : '📍'} {t('player.placement')}: {team.placement} ({team.points} {t('player.points')})
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-4">
                                                {team.members
                                                    .slice()
                                                    .sort((a, b) => (b.avgScore ?? 0) - (a.avgScore ?? 0))
                                                    .map(m => (
                                                        <button
                                                            key={m.bowlerId}
                                                            onClick={() => fetchPlayerGameDetails(team.teamId, m.bowlerId)}
                                                            className={`flex flex-col items-center px-3 py-2 rounded-lg border transition-all duration-200 hover:scale-105 cursor-pointer
                                                ${!allZero && team.mvpId === m.bowlerId ? "border-yellow-400 bg-yellow-400/10 hover:bg-yellow-400/20" : "border-red-400/20 bg-red-500/20 hover:bg-red-500/30"}`}
                                                        >
                                                            <span className="font-bold text-white">{m.name}</span>
                                                            <span className="text-sm text-white/80">
                                                                {t('player.avg')}: <span className="font-bold">{typeof m.avgScore === "number" && !isNaN(m.avgScore) ? m.avgScore.toFixed(2) : "0.00"}</span>
                                                            </span>
                                                            {m.overPar && (
                                                                <span className="mt-1 text-xs bg-green-400/40 text-green-900 px-2 py-0.5 rounded-full font-bold border border-green-600/40">
                                                                    {t('player.overParBowling')} ✅
                                                                </span>
                                                            )}
                                                            {!allZero && team.mvpId === m.bowlerId && (
                                                                <span className="mt-1 text-xs bg-orange-400/40 text-yellow-900 px-2 py-0.5 rounded-full font-bold border border-orange-600/20">
                                                                    {t('player.mvp')} 👑
                                                                </span>
                                                            )}
                                                            <span className="mt-2 text-xs text-blue-400 hover:text-blue-300">
                                                                {t('player.clickForDetails')} 👉
                                                            </span>
                                                        </button>
                                                    ))}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                )}

                {/* Player Game Details Modal */}
                {showPlayerModal && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-hidden border border-white/20 shadow-2xl">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-2xl font-bold text-white">{selectedPlayerDetails?.teamName}</h2>
                                    <p className="text-blue-300 text-sm">{selectedPlayerDetails?.leagueName}</p>
                                </div>
                                <button
                                    onClick={closePlayerModal}
                                    className="text-white hover:text-red-400 transition duration-200"
                                    aria-label="Close modal"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {playerDetailsLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                                </div>
                            ) : selectedPlayerDetails && selectedPlayerDetails.squads.length > 0 ? (
                                <div className="overflow-y-auto max-h-[70vh] pr-2" style={{scrollbarWidth: 'thin', scrollbarColor: 'rgba(59, 130, 246, 0.5) rgba(255, 255, 255, 0.1)'}}>
                                    <div className="space-y-4">
                                        {selectedPlayerDetails.squads.map((squad) => (
                                            <div key={squad.squadId} className="bg-white/10 rounded-lg p-4 border border-white/20">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div>
                                                        <h3 className="text-lg font-bold text-white">{squad.squadName}</h3>
                                                        <p className="text-blue-300 text-sm">{new Date(squad.squadDate).toLocaleDateString()}</p>
                                                    </div>
                                                    <div className="bg-purple-600/30 px-4 py-2 rounded-lg border border-purple-500/50">
                                                        <p className="text-xs text-purple-200">{t('player.seriesTotal')}</p>
                                                        <p className="text-2xl font-bold text-white">{squad.seriesTotal}</p>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                                                    {squad.games.map((game) => (
                                                        <div key={game.gameNumber} className="bg-white/5 rounded-lg p-3 border border-white/10 text-center">
                                                            <p className="text-xs text-blue-200 mb-1">{t('player.game')} {game.gameNumber}</p>
                                                            <p className="text-xl font-bold text-white">{game.score}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <p className="text-white text-lg">{t('player.noGamesRecorded')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* League Financials */}
                <div className="bg-purple-900/30 rounded-xl p-6 border border-purple-500/30 shadow-lg">
                    <h2 className="text-xl font-bold text-white mb-4">{t('player.leagueFinancials')}</h2>
                    
                    {/* All Time Balance Card */}
                    {(() => {
                        const totalBalance = data.leagues.reduce((sum, l) => sum + (l.totalEarned - l.totalSpent), 0);
                        return (
                            <div className={`mb-4 rounded-lg p-3 border w-fit ${totalBalance >= 0 ? 'bg-green-900/30 border-green-500/30' : 'bg-red-900/30 border-red-500/30'}`}>
                                <div className="flex items-center gap-3">
                                    <div className="text-xs text-white/70">{t('player.allTimeBalance')}:</div>
                                    <div className={`text-lg font-bold ${totalBalance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        ${totalBalance.toFixed(2)}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                    
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm text-white">
                            <thead>
                                <tr className="bg-purple-800/60">
                                    <th className="px-4 py-2">{t('league.league')}</th>
                                    <th className="px-4 py-2">{t('player.spent')}</th>
                                    <th className="px-4 py-2">{t('player.earned')}</th>
                                    <th className="px-4 py-2">{t('player.balance')}</th>
                                    <th className="px-4 py-2">{t('player.avgBowled')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.leagues.map(l => (
                                    <tr key={l.leagueId} className="border-b border-purple-700/40 hover:bg-purple-800/30">
                                        <td className="px-4 py-2 font-bold">{l.leagueName}</td>
                                        <td className="px-4 py-2 text-red-300">${l.totalSpent}</td>
                                        <td className="px-4 py-2 text-green-300">${l.totalEarned}</td>
                                        <td className={`px-4 py-2 font-bold ${l.totalEarned - l.totalSpent >= 0 ? "text-green-400" : "text-red-400"}`}>
                                            ${l.totalEarned - l.totalSpent}
                                        </td>
                                        <td className="px-4 py-2 text-yellow-300">{l.avgScore?.toFixed(2) ?? "0.00"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>


                {/* Side Event Earnings */}
                {filteredSideEvents.length > 0 && (
                    <div className="bg-purple-900/30 rounded-xl p-6 border border-purple-500/30 shadow-lg">
                        <h2 className="text-xl font-bold text-white mb-4">{t('player.sideEventEarnings')}</h2>
                        
                        {/* Most/Least Earned Stat Cards */}
                        {(() => {
                            const typeEarnings: { [type: string]: number } = {};
                            filteredSideEvents.forEach(se => {
                                const translatedType = getSideEventTypeTranslation(se.sideEventType);
                                if (!typeEarnings[translatedType]) typeEarnings[translatedType] = 0;
                                typeEarnings[translatedType] += Number(se.totalEarned);
                            });
                            
                            const entries = Object.entries(typeEarnings);
                            if (entries.length === 0) return null;
                            
                            const mostEarnedType = entries.reduce((max, curr) => curr[1] > max[1] ? curr : max);
                            const leastEarnedType = entries.reduce((min, curr) => curr[1] < min[1] ? curr : min);
                            
                            return (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    <div className="bg-green-900/30 rounded-lg p-4 border border-green-500/30">
                                        <div className="text-sm text-green-300 mb-1">{t('player.mostEarned')}</div>
                                        <div className="text-lg font-bold text-white">
                                            {mostEarnedType[0]} <span className="text-green-400">(${mostEarnedType[1].toFixed(2)})</span>
                                        </div>
                                    </div>
                                    <div className="bg-red-900/30 rounded-lg p-4 border border-red-500/30">
                                        <div className="text-sm text-red-300 mb-1">{t('player.leastEarned')}</div>
                                        <div className="text-lg font-bold text-white">
                                            {leastEarnedType[0]} <span className="text-red-400">(${leastEarnedType[1].toFixed(2)})</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                        
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm text-white">
                                <thead>
                                    <tr className="bg-purple-800/60">
                                        <th className="px-4 py-2">{t('league.type')}</th>
                                        <th className="px-4 py-2">{t('player.totalEarned')}</th>
                                        <th className="px-4 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(groupedSideEvents).map(([type, events]) => {
                                        const sum = events.reduce((acc, se) => acc + Number(se.totalEarned), 0);
                                        return (
                                            <React.Fragment key={type}>
                                                <tr
                                                    className="border-b border-purple-700/40 cursor-pointer hover:bg-purple-800/30"
                                                    onClick={() => toggleSideEventType(type)}
                                                >
                                                    <td className="px-4 py-2 font-bold">{getSideEventTypeTranslation(type)}</td>
                                                    <td className="px-4 py-2 text-green-300">${sum}</td>
                                                    <td className="px-4 py-2 text-right">
                                                        <span className="text-purple-300">
                                                            {openSideEventTypes[type] ? "▲" : "▼"}
                                                        </span>
                                                    </td>
                                                </tr>
                                                {openSideEventTypes[type] && events.map(se => (
                                                    <tr key={se.sideEventId} className="bg-purple-800/20">
                                                        <td className="px-8 py-2">{se.sideEventName}</td>
                                                        <td className="px-4 py-2 text-green-200">${se.totalEarned}</td>
                                                        <td className="px-4 py-2"></td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlayerAnalytics;